import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import WalletsPage from "@/pages/app/Wallets";
import KycPage from "@/pages/app/Kyc";
import DigioKycCallback from "@/pages/app/DigioKycCallback";
import TruidKycCallback from "@/pages/app/TruidKycCallback";
import OtcPage from "@/pages/app/Otc";
import SupportPage from "@/pages/app/Support";
import ProfileDashboard from "@/pages/app/profile/ProfileDashboard";
import ProfileHistory from "@/pages/app/profile/ProfileHistory";
import ProfileTax from "@/pages/app/profile/ProfileTax";
import ProfileTransfer from "@/pages/app/profile/ProfileTransfer";
import ProfileSessions from "@/pages/app/profile/ProfileSessions";
import ProfileSettings from "@/pages/app/profile/ProfileSettings";
import RewardsPage from "@/pages/app/Rewards";
import P2PMarketplace from "@/pages/app/P2PMarketplace";
import P2PMyAds from "@/pages/app/P2PMyAds";
import P2PPaymentMethods from "@/pages/app/P2PPaymentMethods";
import P2POrderDetail from "@/pages/app/P2POrderDetail";
import P2POrders from "@/pages/app/P2POrders";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/Dashboard";
import Market from "@/pages/Market";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import RiskDisclosure from "@/pages/RiskDisclosure";
import AmlPolicy from "@/pages/AmlPolicy";
import Fees from "@/pages/Fees";
import AdminUsers from "@/pages/admin/Users";
import AdminUserDetail from "@/pages/admin/UserDetail";
import AdminAccessControl from "@/pages/admin/AccessControl";
import AdminWalletManagement from "@/pages/admin/WalletManagement";
import AdminWithdrawalApprovals from "@/pages/admin/WithdrawalApprovals";
import AdminWalletWhitelistApprovals from "@/pages/admin/WalletWhitelistApprovals";
import AdminDepositWhitelistApprovals from "@/pages/admin/DepositWhitelistApprovals";
import AdminP2PDisputes from "@/pages/admin/P2PDisputes";
import AdminFeeManagement from "@/pages/admin/FeeManagement";
import AdminRiskManagement from "@/pages/admin/RiskManagement";
import AdminMarkets from "@/pages/admin/Markets";
import AdminFiuAml from "@/pages/admin/FiuAml";
import AdminMarketSurveillance from "@/pages/admin/MarketSurveillance";
import AdminRiskCompliance from "@/pages/admin/RiskCompliance";

import AdminReports from "@/pages/admin/ReportsAnalytics";
import AdminCms from "@/pages/admin/CmsAdmin";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminSecurity from "@/pages/admin/SecurityControls";
import AdminSmsDltSettings from "@/pages/admin/SmsDltSettings";
import AdminTax from "@/pages/admin/TaxationGst";
import AdminTdsSettings from "@/pages/admin/TdsSettings";
import AdminTdsTransactions from "@/pages/admin/TdsTransactions";
import AdminTdsReports from "@/pages/admin/TdsReports";
import AdminSupport from "@/pages/admin/SupportAdmin";
import AdminKyc from "@/pages/admin/KycReview";
import AdminSignzySettings from "@/pages/admin/SignzySettings";
import AdminOtc from "@/pages/admin/OtcDesk";
import AdminRewardsBonuses from "@/pages/admin/RewardsBonuses";
import LaunchGateGuard from "@/components/LaunchGateGuard";
import ScrollToTop from "@/components/ScrollToTop";
import AdminLaunchGate from "@/pages/admin/AdminLaunchGate";
import RequireAuth from "@/components/RequireAuth";
import { AuthProvider } from "@/lib/auth";
import { AdminAuthProvider } from "@/lib/adminAuth";

const guarded = (el) => <RequireAuth>{el}</RequireAuth>;

function RedirectKeepQuery({ to, search: extra = "" }) {
  const { search, hash } = useLocation();
  const params = new URLSearchParams(search);
  new URLSearchParams(extra).forEach((v, k) => {
    if (!params.has(k)) params.set(k, v);
  });
  const qs = params.toString();
  return <Navigate to={`${to}${qs ? `?${qs}` : ""}${hash}`} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
          <BrowserRouter>
        <AdminAuthProvider>
          <ScrollToTop />
          <LaunchGateGuard>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/markets" element={<Market />} />
            <Route path="/trade/*" element={<Navigate to="/markets" replace />} />
            <Route path="/contact" element={<SupportPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* App (auth required — must have email + mobile verified) */}
            <Route path="/wallets" element={guarded(<WalletsPage />)} />
            <Route path="/otc" element={guarded(<OtcPage />)} />
            <Route path="/kyc/digio/callback" element={guarded(<DigioKycCallback />)} />
            <Route path="/kyc/truid/callback" element={guarded(<TruidKycCallback />)} />

            {/* Account (PaisaCX profile layout) */}
            <Route path="/profile" element={guarded(<ProfileDashboard />)} />
            <Route path="/profile/kyc" element={guarded(<KycPage />)} />
            <Route path="/profile/history" element={guarded(<ProfileHistory />)} />
            <Route path="/profile/tax" element={guarded(<ProfileTax />)} />
            <Route path="/profile/transfer" element={guarded(<ProfileTransfer />)} />
            <Route path="/profile/sessions" element={guarded(<ProfileSessions />)} />
            <Route path="/profile/settings" element={guarded(<ProfileSettings />)} />

            {/* Legacy paths */}
            <Route path="/dashboard" element={<RedirectKeepQuery to="/profile" />} />
            <Route path="/profile/rewards" element={guarded(<RewardsPage />)} />
            <Route path="/rewards" element={<Navigate to="/profile/rewards" replace />} />
            <Route path="/kyc" element={<RedirectKeepQuery to="/profile/kyc" />} />
            <Route path="/activity" element={<RedirectKeepQuery to="/profile/sessions" />} />
            <Route path="/settings" element={<RedirectKeepQuery to="/profile/settings" />} />
            <Route path="/transfer" element={<RedirectKeepQuery to="/profile/transfer" />} />
            <Route path="/support" element={<RedirectKeepQuery to="/contact" />} />

            {/* P2P (Phase E) — auth required (KYC enforced server-side per action) */}
            <Route path="/p2p" element={guarded(<P2PMarketplace />)} />
            <Route path="/p2p/orders" element={guarded(<P2POrders />)} />
            <Route path="/p2p/my-ads" element={guarded(<P2PMyAds />)} />
            <Route path="/p2p/payment-methods" element={guarded(<P2PPaymentMethods />)} />
            <Route path="/p2p/ad/:adId" element={guarded(<P2PMarketplace />)} />
            <Route path="/p2p/orders/:orderId" element={guarded(<P2POrderDetail />)} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/spot" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/futures" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/users/:id" element={<AdminUserDetail />} />
            <Route path="/admin/access-control" element={<AdminAccessControl />} />
            <Route path="/admin/wallet" element={<AdminWalletManagement />} />
            <Route path="/admin/wallet/approvals" element={<AdminWithdrawalApprovals />} />
            <Route path="/admin/wallet/whitelist-approvals" element={<AdminWalletWhitelistApprovals />} />
            <Route path="/admin/wallet/deposit-whitelist-approvals" element={<AdminDepositWhitelistApprovals />} />
            <Route path="/admin/p2p/disputes" element={<AdminP2PDisputes />} />
            <Route path="/admin/fees" element={<AdminFeeManagement />} />
            <Route path="/admin/markets" element={<AdminMarkets />} />
            <Route path="/admin/risk" element={<AdminRiskManagement />} />
            <Route path="/admin/aml" element={<AdminFiuAml />} />
            <Route path="/admin/surveillance" element={<AdminMarketSurveillance />} />
            <Route path="/admin/compliance" element={<AdminRiskCompliance />} />

            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/cms" element={<AdminCms />} />
            <Route path="/admin/launch-gate" element={<AdminLaunchGate />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/admin/security" element={<AdminSecurity />} />
            <Route path="/admin/security/sms-dlt" element={<AdminSmsDltSettings />} />
            <Route path="/admin/security/ip-whitelist" element={<Navigate to="/admin/access-control?tab=ip_logs" replace />} />
            <Route path="/admin/security/access-logs" element={<Navigate to="/admin/access-control?tab=ip_logs" replace />} />
            <Route path="/admin/tax" element={<AdminTax />} />
            <Route path="/admin/finance/tds" element={<AdminTdsSettings />} />
            <Route path="/admin/finance/tds/transactions" element={<AdminTdsTransactions />} />
            <Route path="/admin/reports/tds" element={<AdminTdsReports />} />
            <Route path="/admin/support" element={<AdminSupport />} />
            <Route path="/admin/kyc" element={<AdminKyc />} />
            <Route path="/admin/kyc/signzy" element={<AdminSignzySettings />} />
            <Route path="/admin/otc" element={<AdminOtc />} />
            <Route path="/admin/marketing/rewards" element={<AdminRewardsBonuses />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/about" element={<Navigate to="/" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/risk-disclosure" element={<RiskDisclosure />} />
            <Route path="/aml-policy" element={<AmlPolicy />} />
            <Route path="/fees" element={<Fees />} />
          </Routes>
          </LaunchGateGuard>
        </AdminAuthProvider>
          </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
